import { logManager } from '../logs'

// Libraries.
import EventEmitter from 'eventemitter3'

/*
 * A conversion from MediaDeviceInfo.kind values to their more common terms.
 * See: https://developer.mozilla.org/en-US/docs/Web/API/MediaDeviceInfo/kind
 * @enum {String}
 */
export const WEBRTC_DEVICE_KINDS = {
  audioinput: 'microphone',
  videoinput: 'camera',
  audiooutput: 'speaker'
}

/**
 * Manager for connected devices.
 * Keeps an up-to-date list of all devices.
 * @class DeviceManager
 */
export default function DeviceManager () {
  const log = logManager.getLogger('Manager', 'Device')

  // Internal variables.
  const emitter = new EventEmitter()
  // Store each device type separately, so that `deviceId` is unique
  //    per kind (there is a `default` deviceId per kind).
  let microphone = []
  let camera = []
  let speaker = []

  // Check devices on initialization.
  checkDevices().then(() => {
    // Emit an initial event to notify that devices are available.
    emitter.emit('change')
  })

  // Check devices whenever they change.
  let recentDeviceChange = false
  navigator.mediaDevices.addEventListener('devicechange', () => {
    log.info('Media device change detected.')
    // A physical device change results in one event per
    // device "kind". Group the events together.
    if (!recentDeviceChange) {
      recentDeviceChange = true
      setTimeout(() => {
        recentDeviceChange = false
        checkDevices().then(() => {
          // Emit an event to notify of the change.
          emitter.emit('change')
        })
      }, 50)
    }
  })

  /**
   * Updates the stored device lists with the latest devices.
   * @method checkDevices
   * @return {Promise}
   */
  function checkDevices () {
    log.info('Checking media devices.')
    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .enumerateDevices()
        .then(devices => {
          // Clear the stored devices, to prevent duplicates.
          microphone = []
          camera = []
          speaker = []
          devices.forEach(device => {
            const kind = WEBRTC_DEVICE_KINDS[device.kind]
            switch (kind) {
              case 'microphone':
                microphone.push(device)
                break
              case 'camera':
                camera.push(device)
                break
              case 'speaker':
                speaker.push(device)
                break
            }
          })
          resolve(get())
        })
        .catch(reject)
    })
  }

  /**
   * Ask for permission and gets the list of available device(s) available from the end-user's devices.
   * @method setupDeviceInitialization
   * @param browserConstraints
   * @return {Object}
   */

  function setupDeviceInitialization (browserConstraints) {
    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .getUserMedia(browserConstraints)
        .then(mediaStream => {
          mediaStream.getTracks().forEach(track => {
            track.stop()
          })
          const devices = checkDevices()
          resolve(devices)
        })
        .catch(reject)
    })
  }

  /**
   * Retrieves the stored device lists.
   * @method get
   * @return {Object}
   */
  function get () {
    return {
      microphone,
      camera,
      speaker
    }
  }

  function on (...args) {
    return emitter.on(...args)
  }

  function once (...args) {
    return emitter.once(...args)
  }

  function off (...args) {
    return emitter.off(...args)
  }

  /**
   * The exposed API.
   */
  return {
    checkDevices,
    setupDeviceInitialization,
    get,
    on,
    once,
    off
  }
}
